import { CommonMessage } from '@frontend/lang';
import { ScreenType } from '@frontend/wizard/types';
import { LuCheck, LuDoorOpen } from 'react-icons/lu';
import { FormattedMessage } from 'react-intl';

import WizardScreenContainer from '../../components/wizard-screen-container/wizard-screen-container.component';
import useProcessOpenCloseSlot from './process-open-close-slot.controller';

const ID = 'process-open-close-slot';

const getInstructionMessage = (screenType?: string | undefined) => {
    switch (screenType) {
        case ScreenType.PROCESS_OPEN_CLOSE_SLOT_DROP_OFF:
            return (
                <FormattedMessage
                    defaultMessage='Drop off your item and close the door.'
                    id='Wizard.ProcessOpenCloseSlot.DropOffInstruction'
                    description='Instruction to the user to drop off their item and close the door.'
                />
            );
        case ScreenType.PROCESS_OPEN_CLOSE_SLOT_PICK_UP:
            return (
                <FormattedMessage
                    defaultMessage='Pick up your item and close the door.'
                    id='Wizard.ProcessOpenCloseSlot.PickUpInstruction'
                    description='Instruction to the user to pick up their item and close the door.'
                />
            );
        case ScreenType.PROCESS_OPEN_CLOSE_SLOT_SHOP:
            return (
                <FormattedMessage
                    defaultMessage='Take your product and close the door.'
                    id='Wizard.ProcessOpenCloseSlot.ShopInstruction'
                    description='Instruction to the user to take their product and close the door.'
                />
            );
        case ScreenType.PROCESS_OPEN_CLOSE_SLOT_RETURN:
            return (
                <FormattedMessage
                    defaultMessage='Drop off your product and close the door.'
                    id='Wizard.ProcessOpenCloseSlot.ReturnInstruction'
                    description='Instruction to the user to drop off their product and close the door.'
                />
            );
        case ScreenType.PROCESS_OPEN_CLOSE_SLOT_REVIEW:
            return (
                <FormattedMessage
                    defaultMessage='Drop off your reviewed product and close the door.'
                    id='Wizard.ProcessOpenCloseSlot.ReviewInstruction'
                    description='Instruction to the user to drop off their reviewed product and close the door.'
                />
            );
        default:
            return (
                <FormattedMessage
                    defaultMessage='Please close the door when you are done.'
                    id='Wizard.ProcessOpenCloseSlot.CloseDoorInstruction'
                    description='Instruction to the user to close the door after opening the slot.'
                />
            );
    }
};

const ProcessOpenCloseSlot = () => {
    const viewProps = useProcessOpenCloseSlot();
    const instructionMessage = getInstructionMessage(viewProps.screen_type);
    //opening
    //not opened
    //not closed

    //TODO: for now just show this in any other state to be safe
    let content = (
        <>
            <div className='bg-brand-secondary flex size-24 items-center justify-center overflow-hidden rounded-full'>
                <LuDoorOpen className='text-background-surface size-10' />
            </div>
            <div className='flex flex-col items-center justify-start gap-1 self-stretch'>
                <div className='text-foreground-default justify-start text-xl font-semibold'>
                    <FormattedMessage
                        defaultMessage='SLOT {slot} is now open!'
                        id='Wizard.ProcessOpenCloseSlot.SlotOpen'
                        description='Message displayed to inform the user that the SLOT is now open.'
                        values={{
                            slot: viewProps.slot?.display
                        }}
                    />
                </div>
                <div className='text-brand-muted justify-start self-stretch text-center text-base font-medium'>{instructionMessage}</div>
            </div>
        </>
    );

    if (viewProps.slotStatus === 'opened') {
        if (viewProps.waitingForSeconds !== null && viewProps.waitingForSeconds >= 30) {
            content = (
                <>
                    <div className='bg-status-warning flex size-24 items-center justify-center overflow-hidden rounded-full'>
                        <LuDoorOpen className='text-background-surface size-10' />
                    </div>
                    <div className='flex flex-col items-center justify-start gap-1 self-stretch'>
                        <div className='text-foreground-default justify-start text-xl font-semibold'>
                            <FormattedMessage
                                defaultMessage='SLOT {slot} is still open!'
                                id='Wizard.ProcessOpenCloseSlot.SlotStillOpen'
                                description='Message displayed to inform the user that the SLOT is still open after a certain period.'
                                values={{
                                    slot: viewProps.slot?.display
                                }}
                            />
                        </div>
                        <div className='text-brand-muted justify-start self-stretch text-center text-base font-medium'>{instructionMessage}</div>
                    </div>
                </>
            );
        } else {
            content = (
                <>
                    <div className='bg-brand-secondary flex size-24 items-center justify-center overflow-hidden rounded-full'>
                        <LuDoorOpen className='text-background-surface size-10' />
                    </div>
                    <div className='flex flex-col items-center justify-start gap-1 self-stretch'>
                        <div className='text-foreground-default justify-start text-xl font-semibold'>
                            <FormattedMessage
                                defaultMessage='SLOT {slot} is now open!'
                                id='Wizard.ProcessOpenCloseSlot.SlotOpen'
                                description='Message displayed to inform the user that the SLOT is now open.'
                                values={{
                                    slot: viewProps.slot?.display
                                }}
                            />
                        </div>
                        <div className='text-brand-muted justify-start self-stretch text-center text-base font-medium'>{instructionMessage}</div>
                    </div>
                </>
            );
        }
    }

    if (viewProps.slotStatus === 'closed') {
        content = (
            <>
                <div className='bg-status-success flex size-24 items-center justify-center overflow-hidden rounded-full'>
                    <LuCheck className='text-background-surface size-10' />
                </div>
                <div className='flex flex-col items-center justify-start gap-1 self-stretch'>
                    <div className='text-foreground-default justify-start text-xl font-semibold'>
                        <FormattedMessage
                            defaultMessage='SLOT {slot} closed!'
                            id='Wizard.ProcessOpenCloseSlot.SlotClosed'
                            description='Message displayed to inform the user that the SLOT has been closed.'
                            values={{
                                slot: viewProps.slot?.display
                            }}
                        />
                    </div>

                    <div className='text-brand-muted justify-start self-stretch text-center'>
                        <FormattedMessage
                            defaultMessage='Thank you!'
                            id='Wizard.ProcessOpenCloseSlot.ExitInstruction'
                            description='Instruction to the user that they can exit now after closing the slot.'
                        />
                    </div>
                </div>
                {viewProps.showCenterContinue && (
                    <button
                        type='button'
                        className='bg-brand-primary text-background-surface flex items-center justify-center rounded-xl px-4 py-3 text-base font-medium'
                        onClick={viewProps.onCenterContinue}>
                        {viewProps.nextStep ? CommonMessage.BUTTONS.CONTINUE : CommonMessage.BUTTONS.EXIT}
                    </button>
                )}
            </>
        );
    }

    return (
        <WizardScreenContainer
            id={ID}
            footer={{
                onSubmit: viewProps.onSubmit,
                submitLabel: viewProps.nextStep ? CommonMessage.BUTTONS.CONTINUE : CommonMessage.BUTTONS.EXIT
            }}>
            <div className='flex size-full flex-col items-center justify-center gap-4'>{content}</div>
        </WizardScreenContainer>
    );
};

export default ProcessOpenCloseSlot;
